import ExcelJS from 'exceljs';

const MAX_CELL_LENGTH = 32765;

const normalizeLength = (value: string): string => {
    if (value.length > MAX_CELL_LENGTH) {
        return value.substring(0, MAX_CELL_LENGTH);
    }
    return value;
};

const createRobotoFont = (isBold: boolean, size?: number): Partial<ExcelJS.Font> => {
    return {
        name: 'Roboto',
        size: size ?? 11,
        bold: isBold,
    };
};

export type CellStyle = {
    bold?: boolean;
    align?: 'left' | 'center' | 'right' | 'fill' | 'justify' | 'centerContinuous' | 'distributed';
    verticalAlign?: 'top' | 'middle' | 'bottom';
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    // Underline the cell text
    underline?: boolean;
    // Border styles; 'all' applies to all sides unless overridden by a specific side
    border?: {
        all?: {
            style?:
                | 'thin'
                | 'dotted'
                | 'dashDot'
                | 'hair'
                | 'medium'
                | 'thick'
                | 'double'
                | 'slantDashDot'
                | 'dashDotDot'
                | 'dashed'
                | 'mediumDashed'
                | 'mediumDashDot'
                | 'mediumDashDotDot';
            color?: string; // hex or ARGB
        };
        top?: { style?: CellStyle['border']['all']['style']; color?: string };
        right?: { style?: CellStyle['border']['all']['style']; color?: string };
        bottom?: { style?: CellStyle['border']['all']['style']; color?: string };
        left?: { style?: CellStyle['border']['all']['style']; color?: string };
    };
    // Wrap text within the cell
    wrapText?: boolean;
    // Merge this cell across N columns on the same row (including this cell). If provided, defaults alignment to center.
    mergeAcross?: number;
    // Merge this cell down N rows in the same column (including this cell). Can be combined with mergeAcross.
    mergeDown?: number;
};

// Normalize color strings to ARGB (AARRGGBB) expected by ExcelJS
// Accepts formats like '#RRGGBB', 'RRGGBB', 'AARRGGBB', '#RGB', '#ARGB'
const toARGB = (input?: string): string | undefined => {
    if (!input) return undefined;
    let s = input.trim().replace(/^#/, '').toUpperCase();

    // Expand shorthand #RGB or #ARGB
    if (s.length === 3) {
        s = s
            .split('')
            .map((c) => c + c)
            .join(''); // RRGGBB
        return 'FF' + s; // add full alpha
    }
    if (s.length === 4) {
        // ARGB -> expand each nibble
        const a = s[0] + s[0];
        const r = s[1] + s[1];
        const g = s[2] + s[2];
        const b = s[3] + s[3];
        return a + r + g + b;
    }

    if (s.length === 6) {
        // RRGGBB -> add FF alpha
        return 'FF' + s;
    }
    if (s.length === 8) {
        // Already AARRGGBB
        return s;
    }
    // Unsupported format; return undefined to avoid setting invalid color
    return undefined;
};

// Apply common font and alignment styling
const applyCellStyle = (cell: ExcelJS.Cell, cellStyle?: CellStyle): void => {
    const font = createRobotoFont(cellStyle?.bold, cellStyle?.fontSize);
    cell.font = font;

    // Build alignment from provided options (merge with existing)
    const alignment: Partial<ExcelJS.Alignment> = { ...cell.alignment } as Partial<ExcelJS.Alignment>;
    if (cellStyle?.align) {
        alignment.horizontal = cellStyle.align;
    }
    if (cellStyle?.verticalAlign) {
        alignment.vertical = cellStyle.verticalAlign as ExcelJS.Alignment['vertical'];
    }
    if (typeof cellStyle?.wrapText === 'boolean') {
        alignment.wrapText = cellStyle.wrapText;
    }
    if (Object.keys(alignment).length > 0) {
        cell.alignment = alignment as ExcelJS.Alignment;
    }
    if (cellStyle?.fontSize) {
        // ensure font size is set if provided
        cell.font.size = cellStyle.fontSize;
    }
    if (cellStyle?.underline) {
        // apply underline if requested
        cell.font = { ...cell.font, underline: true } as ExcelJS.Font;
    }
    const fg = toARGB(cellStyle?.color);
    if (fg) {
        // apply font color if valid
        cell.font = { ...cell.font, color: { argb: fg } } as ExcelJS.Font;
    }
    const bg = toARGB(cellStyle?.backgroundColor);
    if (bg) {
        // apply background color if valid
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: bg },
        } as ExcelJS.FillPattern;
    }

    // Apply borders if specified
    if (cellStyle?.border) {
        const buildSide = (
            side?: { style?: NonNullable<CellStyle['border']>['all']['style']; color?: string },
            all?: NonNullable<CellStyle['border']>['all']
        ) => {
            const style = side?.style ?? all?.style;
            const color = toARGB(side?.color ?? all?.color);
            if (!style && !color) return undefined;
            return {
                style: (style ?? 'thin') as any,
                ...(color ? { color: { argb: color } } : {}),
            };
        };

        const all = cellStyle.border.all;
        const top = buildSide(cellStyle.border.top, all);
        const right = buildSide(cellStyle.border.right, all);
        const bottom = buildSide(cellStyle.border.bottom, all);
        const left = buildSide(cellStyle.border.left, all);

        const anySide = top || right || bottom || left;
        if (anySide) {
            cell.border = {
                ...(top ? { top } : {}),
                ...(right ? { right } : {}),
                ...(bottom ? { bottom } : {}),
                ...(left ? { left } : {}),
            } as any;
        }
    }
};

// Unified setter: accepts string or number and applies shared styling
const setCellValueWithFont = (worksheet: ExcelJS.Worksheet, row: number, column: number, cellValue: string | number, cellStyle?: CellStyle) => {
    if (cellValue === null || cellValue === undefined) return;

    // Handle merge (across columns and/or down rows) & center if requested
    const across = cellStyle?.mergeAcross && cellStyle.mergeAcross > 1 ? cellStyle.mergeAcross : 1;
    const down = cellStyle?.mergeDown && cellStyle.mergeDown > 1 ? cellStyle.mergeDown : 1;
    if (across > 1 || down > 1) {
        const toCol = column + across - 1;
        const toRow = row + down - 1;
        worksheet.mergeCells(row, column, toRow, toCol);
        // Default to centered text when merging, unless caller already specified alignment
        if (!cellStyle?.align) {
            cellStyle = { ...cellStyle, align: 'center' };
        }
    }

    const cell = worksheet.getCell(row, column);

    if (typeof cellValue === 'number') {
        // keep numeric values as numbers
        cell.value = cellValue;
        applyCellStyle(cell, cellStyle);
        return cell;
    } else {
        const trimmed = cellValue.trim();
        if (trimmed === '') {
            // If empty string: only skip when no styling is requested; otherwise apply styles to enable borders/merges/etc.
            const hasStyle = !!cellStyle && Object.keys(cellStyle).length > 0;
            if (!hasStyle) return;
            cell.value = '';
            applyCellStyle(cell, cellStyle);
            return cell;
        }
        cell.value = normalizeLength(cellValue);
        applyCellStyle(cell, cellStyle);
        return cell;
    }
};

const setNumberOfDecimals = (val: number | null, numberOfDec: number): number | null => {
    if (val !== null && val !== undefined) {
        return Math.round(val * Math.pow(10, numberOfDec)) / Math.pow(10, numberOfDec);
    }
    return null;
};

// Configure worksheet to print on a single page width and sensible margins
const setupWorksheetForPrint = (worksheet: ExcelJS.Worksheet): void => {
    worksheet.pageSetup = {
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 1, // fit to a single page tall to use full vertical space
        orientation: 'portrait',
        horizontalCentered: true,
        verticalCentered: false,
        // A4 paper size; change if you target Letter: 1 (Letter), 9 (A4)
        paperSize: 9 as any,
        margins: {
            left: 0.3,
            right: 0.3,
            top: 0.25,
            bottom: 0.25,
            header: 0.1,
            footer: 0.1,
        },
    } as ExcelJS.PageSetup;
};

// Define the print area to the exact used range to ensure scaling works as expected
const setWorksheetPrintArea = (worksheet: ExcelJS.Worksheet, endRow: number, endCol: number = 9, startRow = 1, startCol = 1): void => {
    if (!endRow || endRow < startRow) return;
    const startAddr = worksheet.getCell(startRow, startCol).address;
    const endAddr = worksheet.getCell(endRow, endCol).address;
    worksheet.pageSetup.printArea = `${startAddr}:${endAddr}` as any;
};

export {
    setCellValueWithFont,
    createRobotoFont,
    normalizeLength,
    setNumberOfDecimals,
    MAX_CELL_LENGTH,
    setupWorksheetForPrint,
    setWorksheetPrintArea,
};
